import type { PickByValueExact } from 'utility-types'

import { capture, type EventListener, type ReadonlyRecord } from '../../../utils'

import { Capability, ConnectwareError, ConnectwareErrorType, ResourceType, Translation } from '../../../domain'

import type { ConnectwareResourcesManagementService, TranslationService } from '../../../application'

import { type ConnectwareHTTPServiceOptions, FetchConnectwareHTTPService, type HttpRequestArgs } from '../Base'

type Action = keyof PickByValueExact<ConnectwareResourcesManagementService, (type: ResourceType, ids: readonly string[]) => Promise<void>>
type Strategy = (action: Action, ids: readonly string[]) => Promise<void>[] | null

export class ConnectwareHTTPResourcesManagementService extends FetchConnectwareHTTPService implements ConnectwareResourcesManagementService {
    private readonly strategies: ReadonlyRecord<ResourceType, Strategy> = {
        [ResourceType.SERVICE]: (action, ids) => {
            const errorTranslations = {
                enable: Translation.BULK_ACTION_ENABLE_SERVICES_ERROR,
                disable: Translation.BULK_ACTION_DISABLE_SERVICES_ERROR,
                delete: Translation.BULK_ACTION_DELETE_SERVICES_ERROR,
            } as const

            switch (action) {
                case 'disable':
                case 'enable':
                    return ids.map((id) =>
                        this.request<'/api/services/+/operation', 'PUT', void>({
                            capability: Capability.SERVICES_MANAGE,
                            method: 'PUT',
                            path: '/api/services/+/operation',
                            pathParams: [id],
                            body: { operation: action === 'enable' ? 'enable' : 'disable' },
                            authenticate: true,
                            handlers: {
                                200: ConnectwareHTTPResourcesManagementService.createSuccessHandler(),
                                400: this.createErrorHandler(errorTranslations[action], ids, action),
                                404: this.createErrorHandler(errorTranslations[action], ids, action),
                            },
                        })
                    )
                case 'delete':
                    return ids.map((id) =>
                        this.request<'/api/services/+', 'DELETE', void>({
                            capability: Capability.SERVICES_MANAGE,
                            method: 'DELETE',
                            path: '/api/services/+',
                            pathParams: [id],
                            authenticate: true,
                            handlers: {
                                200: ConnectwareHTTPResourcesManagementService.createSuccessHandler(),
                                400: this.createErrorHandler(errorTranslations[action], ids, action),
                            },
                        })
                    )
                default:
                    return null
            }
        },
        [ResourceType.ENDPOINT]: this.createResourcesStrategy(
            { capability: Capability.ENDPOINTS_MANAGE, method: 'PUT', path: '/api/v2/resources/+/operation' },
            { enable: Translation.BULK_ACTION_ENABLE_ENDPOINTS_ERROR, disable: Translation.BULK_ACTION_DISABLE_ENDPOINTS_ERROR }
        ),
        [ResourceType.CONNECTION]: this.createResourcesStrategy(
            { capability: Capability.CONNECTIONS_MANAGE, method: 'PUT', path: '/api/v2/resources/+/operation' },
            { enable: Translation.BULK_ACTION_CONNECT_CONNECTIONS_ERROR, disable: Translation.BULK_ACTION_DISCONNECT_CONNECTIONS_ERROR }
        ),
        [ResourceType.MAPPING]: this.createResourcesStrategy(
            { capability: Capability.MAPPINGS_MANAGE, method: 'PUT', path: '/api/v2/resources/+/operation' },
            { enable: Translation.BULK_ACTION_ENABLE_MAPPINGS_ERROR, disable: Translation.BULK_ACTION_DISABLE_MAPPINGS_ERROR }
        ),
        [ResourceType.CORE_CONTAINER]: (action, ids) => {
            const errorTranslations = {
                enable: Translation.BULK_ACTION_ENABLE_CONTAINERS_ERROR,
                disable: Translation.BULK_ACTION_DISABLE_CONTAINERS_ERROR,
                reenable: Translation.BULK_ACTION_RE_ENABLE_CONTAINERS_ERROR,
            } as const

            switch (action) {
                case 'enable':
                case 'disable':
                case 'reenable':
                    return ids.map((id) =>
                        this.request<'/api/core-containers/+/operation', 'PUT', void>({
                            capability: Capability.CORE_CONTAINERS_MANAGE,
                            method: 'PUT',
                            path: '/api/core-containers/+/operation',
                            pathParams: [id],
                            authenticate: true,
                            body: { operation: action === 'enable' ? 'start' : action === 'disable' ? 'stop' : 'restart' },
                            handlers: {
                                200: ConnectwareHTTPResourcesManagementService.createSuccessHandler(),
                                400: this.createErrorHandler(errorTranslations[action], ids, action),
                                404: this.createErrorHandler(errorTranslations[action], ids, action),
                            },
                        })
                    )
                default:
                    return null
            }
        },
        [ResourceType.SERVICE_CONTAINER]: (action, ids) => {
            const errorTranslations = {
                enable: Translation.BULK_ACTION_ENABLE_CONTAINERS_ERROR,
                disable: Translation.BULK_ACTION_DISABLE_CONTAINERS_ERROR,
                reenable: Translation.BULK_ACTION_RE_ENABLE_CONTAINERS_ERROR,
            } as const

            switch (action) {
                case 'enable':
                case 'disable':
                case 'reenable':
                    return ids.map((id) =>
                        this.request<'/api/containers/+/operation', 'PUT', void>({
                            capability: Capability.SERVICE_CONTAINERS_MANAGE,
                            method: 'PUT',
                            path: '/api/containers/+/operation',
                            pathParams: [id],
                            authenticate: true,
                            body: { operation: action === 'enable' ? 'start' : action === 'disable' ? 'stop' : 'restart' },
                            handlers: {
                                200: ConnectwareHTTPResourcesManagementService.createSuccessHandler(),
                                400: this.createErrorHandler(errorTranslations[action], ids, action),
                                404: this.createErrorHandler(errorTranslations[action], ids, action),
                            },
                        })
                    )
                default:
                    return null
            }
        },
        [ResourceType.SERVER]: this.createResourcesStrategy(
            { capability: Capability.SERVERS_MANAGE, method: 'PUT', path: '/api/v2/resources/+/operation' },
            { enable: Translation.BULK_ACTION_ENABLE_SERVERS_ERROR, disable: Translation.BULK_ACTION_DISABLE_SERVERS_ERROR }
        ),
        [ResourceType.AGENT]: (action, ids) => {
            switch (action) {
                case 'delete':
                    return ids.map((id) =>
                        this.request<'/api/resources/runners/+', 'DELETE', void>({
                            capability: Capability.AGENTS_MANAGE,
                            method: 'DELETE',
                            path: '/api/resources/runners/+',
                            pathParams: [id],
                            authenticate: true,
                            handlers: {
                                200: ConnectwareHTTPResourcesManagementService.createSuccessHandler(),
                                400: this.createErrorHandler(Translation.BULK_ACTION_DELETE_AGENTS_ERROR, ids, action),
                            },
                        })
                    )
                default:
                    return null
            }
        },
        [ResourceType.VOLUME]: () => null,
    }

    private static createSuccessHandler (): () => Promise<void> {
        return () => Promise.resolve()
    }

    constructor (
        options: ConnectwareHTTPServiceOptions,
        private readonly translationService: TranslationService,
        private readonly changeDoneListeners: Pick<EventListener<void>, 'trigger'>
    ) {
        super(options)
    }

    private createResourcesStrategy (
        requestArgs: Pick<HttpRequestArgs<'/api/v2/resources/+/operation', 'PUT', void>, 'capability' | 'method' | 'path'>,
        errorTranslations: ReadonlyRecord<'enable' | 'disable', Translation>
    ): Strategy {
        return (action, ids) => {
            switch (action) {
                case 'enable':
                case 'disable':
                    return ids.map((id) =>
                        this.request<'/api/v2/resources/+/operation', 'PUT', void>({
                            ...requestArgs,
                            pathParams: [id],
                            body: { operation: action },
                            authenticate: true,
                            handlers: {
                                200: ConnectwareHTTPResourcesManagementService.createSuccessHandler(),
                                400: this.createErrorHandler(errorTranslations[action], ids, action),
                                500: this.createErrorHandler(errorTranslations[action], ids, action),
                            },
                        })
                    )
                default:
                    return null
            }
        }
    }

    private createErrorHandler (translation: Translation, ids: readonly string[], action: Action): () => Promise<never> {
        return () => {
            const translatedError = new ConnectwareError(
                ConnectwareErrorType.SERVER_ERROR,
                this.translationService.translate(translation, { count: ids.length })
            )
            capture(translatedError, this[action])
            return Promise.reject(translatedError)
        }
    }

    private async makeRequest (type: ResourceType, action: Action, ids: readonly string[]): Promise<void> {
        const promises = this.strategies[type]?.(action, ids)

        if (promises === null) {
            return Promise.reject(new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Not implemented.', { type, action, ids }))
        }

        await Promise.all(promises)

        /**
         * Signal to listeners that the changes are done
         */
        this.changeDoneListeners.trigger()
    }

    delete (type: ResourceType, ids: readonly string[]): Promise<void> {
        return this.makeRequest(type, 'delete', ids)
    }

    disable (type: ResourceType, ids: readonly string[]): Promise<void> {
        return this.makeRequest(type, 'disable', ids)
    }

    enable (type: ResourceType, ids: readonly string[]): Promise<void> {
        return this.makeRequest(type, 'enable', ids)
    }

    reenable (type: ResourceType, ids: readonly string[]): Promise<void> {
        return this.makeRequest(type, 'reenable', ids)
    }
}
